import { isObject } from "lodash";
import * as moment from "moment";

export class LeagueHandler {
    public timingToleranceMilliseconds = 0;

    /**
     * Set a custom timing tolerance on the leagueservice. This number will be added to any date of the league
     * in case the user and server clock are not perfectly in sync.
     */
    public setTimingToleranceMilliseconds = function(timingToleranceMilliseconds: number) {
        this.timingToleranceMilliseconds = timingToleranceMilliseconds;
    };

    /**
     * Returns the seconds until a date occurs or 0 if the date is in the past.
     *
     * @param date
     *
     * @returns Number
     */
    public getSecondsUntilDate = function(date: Date) {
        if (date === null) {
            return 0;
        }
        let currentTime = new Date().getTime();
        let time = Math.ceil((date.getTime() + this.timingToleranceMilliseconds - currentTime) / 1000);

        if (time > 0) {
            return time;
        }

        return 0;
    };

    /**
     * Sorts an object or array of leagues by beginDate (asc)
     *
     * @param leagues
     *
     * @returns {Array}
     */
    public sortLeagues = function(leagues) {
        let leagueList: any[] = [];
        for (let idx in leagues) {
            if (leagues.hasOwnProperty(idx) && typeof leagues[idx] === "object" && idx !== "$promise") {
                leagueList.push(leagues[idx]);
            }
        }

        // @TODO: league typings
        leagueList.sort(
            (leagueA: any, leagueB: any): number => {
                const beginLeagueA = new Date(this.getLeagueBeginDate(leagueA)).getTime();
                const beginLeagueB = new Date(this.getLeagueBeginDate(leagueB)).getTime();

                if (beginLeagueA < beginLeagueB) {
                    return -1;
                } else if (beginLeagueA > beginLeagueB) {
                    return 1;
                } else {
                    if (leagueA.id < leagueB.id) {
                        return -1;
                    } else {
                        return 1;
                    }
                }
            }
        );

        return leagueList;
    };

    public getLeagueBeginDate(league) {
        if (isObject(league.timeline) && isObject(league.timeline.inProgress)) {
            return league.timeline.inProgress.begin;
        } else if (isObject(league.season)) {
            return league.season.begin;
        }
    }

    /**
     * Sorts an object or array of leagues by beginDate (asc)
     *
     * @param leagues
     *
     * @returns {Array}
     */
    public sortClosedLeagues = function(leagues) {
        let leagueList = [];
        for (let idx in leagues) {
            if (leagues.hasOwnProperty(idx) && typeof leagues[idx] === "object" && idx !== "$promise") {
                leagueList.push(leagues[idx]);
            }
        }

        // @TODO: league typings
        leagueList.sort(
            (leagueA: any, leagueB: any): number => {
                const endLeagueA = new Date(this.getLeagueEndDate(leagueA)).getTime();
                const endLeagueB = new Date(this.getLeagueEndDate(leagueB)).getTime();

                if (endLeagueA < endLeagueB) {
                    return 1;
                } else if (endLeagueA > endLeagueB) {
                    return -1;
                } else {
                    if (leagueA.name.full < leagueB.name.full) {
                        return 1;
                    } else {
                        return -1;
                    }
                }
            }
        );

        return leagueList;
    };

    public getLeagueEndDate(league) {
        if (isObject(league.timeline) && isObject(league.timeline.finished)) {
            return league.timeline.finished.begin;
        } else if (isObject(league.season)) {
            return league.season.end;
        }
    }

    /**
     * Sorts an object or array of leagues by beginAt (asc)
     *
     * @param matches
     *
     * @returns {Array}
     * @param asc ascending sorting if 1, else descending
     */
    public sortMatchesByStartDate = function(matches, asc = 1) {
        if (asc != 1) {
            asc = -1;
        }
        let matchList = [];
        for (let idx in matches) {
            if (matches.hasOwnProperty(idx) && typeof matches[idx] === "object" && idx !== "$promise") {
                matchList.push(matches[idx]);
            }
        }
        matchList.sort(function(matchA, matchB) {
            let beginLeagueA, beginLeagueB;
            beginLeagueA = new Date(matchA.beginAt).getTime();
            beginLeagueB = new Date(matchB.beginAt).getTime();

            if (beginLeagueA < beginLeagueB) {
                return -asc;
            } else if (beginLeagueA > beginLeagueB) {
                return asc;
            } else {
                if (matchA.id < matchB.id) {
                    return -asc;
                } else {
                    return asc;
                }
            }
        });
        return matchList;
    };

    public sortMatchesByStartDateDesc = function(matches) {
        return this.sortMatchesByStartDate(matches, -1);
    };

    /**
     * Provide a method to return the number of players depending on the state.
     *
     * @param league The league
     */
    public getNumberOfPlayers = function(league: any) {
        if (league.type == "cup" || league.type == "premiership" || league.type == "swiss") {
            if (
                league.state == "signUp" ||
                league.state == "checkIn" ||
                league.state == "lateSignUp" ||
                league.state == "postSignUp" ||
                league.state == "preSignUp"
            ) {
                return league.contestants.signedUp;
            }

            return league.contestants.checkedIn;
        } else {
            return league.contestants.signedUp;
        }
    };

    /**
     * Provides a method to return the current state and the ISO date string of the next change or null if there is no change
     * for the whole week (i.e. the instantchallenger has timeslots back-to-back for the whole week)
     *
     * @param  league            The league
     * @param  currentDate       currentTime Date object or now if not given
     * @return Object            Object containing the current state and the ISO datestring of the next change
     */
    public getTimeTillInstantChallengerChanges = function(league: any, currentDate: Date = new Date()): Object {
        let weekdayStringTranslation = {
            sunday: 0,
            monday: 1,
            tuesday: 2,
            wednesday: 3,
            thursday: 4,
            friday: 5,
            saturday: 6,
        };

        let now: moment.Moment = moment(currentDate).utc();
        let timeslots: any[][] = [];

        if (league.hasOwnProperty("instantChallenger") && league.instantChallenger.enabled) {
            for (let weekModifier = -1; weekModifier <= 1; weekModifier++) {
                for (let weekday in league.instantChallenger.timeSlots) {
                    if (league.instantChallenger.timeSlots[weekday].length === 0) {
                        continue;
                    }

                    for (let slot in league.instantChallenger.timeSlots[weekday]) {
                        let currentSlot = league.instantChallenger.timeSlots[weekday][slot];
                        let w = weekdayStringTranslation[weekday];
                        w = weekModifier * 7 + w;
                        timeslots.push(this.convertApiToArray(currentSlot, w, now));
                    }
                }
            }
        }

        if (timeslots.length === 0) {
            // no time slots defined, but instantchallenger is set to active
            return { state: "closed", next: null };
        }

        timeslots.sort(function(a, b) {
            if (a[0].isSame(b[0], "day")) {
                return 0;
            }
            return a[0].isBefore(b[0]) ? -1 : 1;
        });

        let isOpen = false;
        let lastEnddate: moment.Moment = null;
        for (let t in timeslots) {
            if (!timeslots.hasOwnProperty(t)) {
                continue;
            }
            let s: moment.Moment = timeslots[t][0],
                e: moment.Moment = timeslots[t][1];

            if (isOpen === true) {
                // check if we have a gap
                if (s.unix() - lastEnddate.unix() !== 0) {
                    return {
                        state: "open",
                        next: lastEnddate.seconds(0).toISOString(),
                    };
                }
                lastEnddate = e;
            } else {
                if ((s.isBefore(now) && e.isAfter(now)) || s.isSame(now)) {
                    // we are open right now, find the earliest end date, but keep slots in mind, that are back-to-back
                    // to do that, we need to iterate further and find the first enddate, that is not back-to-back
                    isOpen = true;
                    lastEnddate = e;
                } else if (s.isAfter(now)) {
                    // this can only be if we are closed, so return the start string
                    return { state: "closed", next: s.seconds(0).toISOString() };
                }
            }
        }

        return {
            state: "open",
            next: null,
        };
    };

    private convertApiToArray = function(currentSlot, weekDay, now): moment.Moment[] {
        let timeExplode = currentSlot.startTime.split(":");

        let hour = parseInt(timeExplode[0]),
            minute = parseInt(timeExplode[1]);
        let start = now
            .clone()
            .hours(hour)
            .minutes(minute)
            .day(weekDay);

        let end = start.clone();
        end.add(currentSlot.duration, "seconds");

        return [start, end];
    };
}
